import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { useAuth } from "@clerk/clerk-react";
import {
  budgetsApi,
  VarianceResponse,
  VarianceLine,
} from "../../api/budgetsApi";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency as formatPounds } from "../../utils/formatters";
import { Budget, BudgetStatus, BudgetType } from "@akfdev-com/shared";

// Budget amounts are stored as integer pence (see Constitution Principle 1).
const formatCurrency = (pence: number) => formatPounds((pence ?? 0) / 100);

// Format date range for display
const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const end = new Date(endDate).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return `${start} to ${end}`;
};

const BudgetDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const { getToken } = useAuth();

  const [budget, setBudget] = useState<Budget | null>(null);
  const [variance, setVariance] = useState<VarianceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);

  useEffect(() => {
    const fetchBudgetDetails = async () => {
      // Guard against undefined id or building
      if (!id) {
        console.warn("Budget ID is undefined in URL");
        setError(
          "Invalid budget ID in URL. Please navigate from the budgets list.",
        );
        setLoading(false);
        return;
      }

      if (!currentBuilding?.id) return;

      try {
        setLoading(true);
        const token = await getToken();
        if (!token) {
          throw new Error("Authentication token not available");
        }

        // Fetch budget details
        const budgetData = await budgetsApi.get(currentBuilding.id, id, token);
        setBudget(budgetData);

        // Fetch variance report
        const varianceData = await budgetsApi.getVariance(
          currentBuilding.id,
          id,
          token,
        );
        setVariance(varianceData);
      } catch (err) {
        console.error("Error fetching budget details:", err);
        setError("Failed to load budget details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBudgetDetails();
  }, [id, currentBuilding?.id, getToken]);

  const handleDeleteBudget = async () => {
    if (!id || !currentBuilding?.id) return;

    try {
      setLoading(true);
      const token = await getToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }
      await budgetsApi.delete(currentBuilding.id, id, token);
      navigate("/dashboard/budgets");
    } catch (err) {
      console.error("Error deleting budget:", err);
      setError("Failed to delete budget. Please try again.");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleApproveBudget = async () => {
    if (!id || !currentBuilding?.id || !budget) return;

    try {
      setLoading(true);
      const token = await getToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }
      const updatedBudget = await budgetsApi.approve(
        currentBuilding.id,
        id,
        token,
      );
      setBudget(updatedBudget);
      setShowApproveConfirm(false);
    } catch (err) {
      console.error("Error approving budget:", err);
      setError("Failed to approve budget. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBudget = async () => {
    if (!id || !currentBuilding?.id || !budget) return;

    try {
      setLoading(true);
      const token = await getToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }

      // Calculate new dates: one year from the original period
      const startDate = new Date(budget.startDate);
      const endDate = new Date(budget.endDate);
      startDate.setFullYear(startDate.getFullYear() + 1);
      endDate.setFullYear(endDate.getFullYear() + 1);

      const newBudget = await budgetsApi.copy(
        currentBuilding.id,
        id,
        {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          name: `Copy of ${budget.name}`,
        },
        token,
      );

      navigate(`/dashboard/budgets/${newBudget.id}`);
    } catch (err) {
      console.error("Error copying budget:", err);
      setError("Failed to copy budget. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: BudgetStatus) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "DRAFT":
        return "bg-yellow-100 text-yellow-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getBudgetTypeLabel = (type: BudgetType) => {
    switch (type) {
      case "INCOME":
        return "Income Budget";
      case "EXPENSE":
        return "Expense Budget";
      case "SAVINGS":
        return "Savings Budget";
      default:
        return type;
    }
  };

  if (loading && !budget) {
    return <Spinner message="Loading budget details..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!budget) {
    return <ErrorAlert message="Budget not found" />;
  }

  const budgetProgress =
    budget.totalBudgeted > 0
      ? (budget.totalActual / budget.totalBudgeted) * 100
      : 0;

  return (
    <div className="">
      <PageHeader
        title={budget.name}
        description={`${formatDateRange(
          budget.startDate,
          budget.endDate,
        )} • ${getBudgetTypeLabel(budget.budgetType)}`}
        actions={
          <div className="flex space-x-2">
            {budget.status === "DRAFT" && (
              <button
                onClick={() => setShowApproveConfirm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none"
              >
                Approve Budget
              </button>
            )}
            <button
              onClick={handleCopyBudget}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Copy Budget
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
            >
              Delete
            </button>
          </div>
        }
      />

      {/* Budget Summary */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex flex-wrap justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Budget Summary
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Overview of budget and actual spending.
            </p>
          </div>
          <div className="mt-2 sm:mt-0">
            <span
              className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ${getStatusColor(
                budget.status,
              )}`}
            >
              {budget.status}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Total Budgeted
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(budget.totalBudgeted)}
              </dd>
            </div>

            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Actual to Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(budget.totalActual)} (
                {Math.round(budgetProgress)}% of budget)
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${
                      budgetProgress > 100 ? "bg-red-600" : "bg-primary-600"
                    }`}
                    style={{ width: `${Math.min(budgetProgress, 100)}%` }}
                  ></div>
                </div>
              </dd>
            </div>

            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Remaining</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span
                  className={`font-medium ${
                    budget.totalBudgeted >= budget.totalActual
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatCurrency(budget.totalBudgeted - budget.totalActual)}
                </span>
              </dd>
            </div>

            {budget.approvedBy && budget.approvedAt && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Approved By
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {budget.approvedBy} on{" "}
                  {new Date(budget.approvedAt).toLocaleDateString()}
                </dd>
              </div>
            )}

            {budget.notes && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Notes</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {budget.notes}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Budget Lines / Variance */}
      {variance && (
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Budget Lines & Variance
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Detailed breakdown of budget categories and actual spending.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Category
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Budgeted
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actual
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Variance
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    % Var
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {variance.lines.map((line: VarianceLine) => {
                  const isPositiveVariance = line.variance >= 0;

                  return (
                    <tr key={line.categoryId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {line.categoryName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(line.budgetAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(line.actualAmount)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          isPositiveVariance ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {isPositiveVariance ? "+" : ""}
                        {formatCurrency(line.variance)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          Math.abs(line.variancePercent) > 10
                            ? "text-red-600 font-medium"
                            : "text-gray-500"
                        }`}
                      >
                        {isPositiveVariance ? "+" : ""}
                        {line.variancePercent.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(variance.totalBudgeted)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(variance.totalActual)}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                      variance.totalVariance >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {variance.totalVariance >= 0 ? "+" : ""}
                    {formatCurrency(variance.totalVariance)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {variance.totalBudgeted > 0
                      ? (
                          (variance.totalVariance / variance.totalBudgeted) *
                          100
                        ).toFixed(1)
                      : "0"}
                    %
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Confirm Dialogs */}
      <ConfirmDialog
        isOpen={showApproveConfirm}
        title="Approve Budget"
        message={`Are you sure you want to approve this budget? Once approved, budget lines cannot be modified.`}
        confirmText="Approve"
        onConfirm={handleApproveBudget}
        onClose={() => setShowApproveConfirm(false)}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Budget"
        message={`Are you sure you want to delete this budget? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handleDeleteBudget}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default BudgetDetails;
